import { nanoid } from 'nanoid';

export interface SecurityEvent {
  id: string;
  timestamp: number;
  type: 'authentication' | 'authorization' | 'input_validation' | 'csp_violation' | 'rate_limit' | 'suspicious_activity' | 'system_error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  userId?: string | undefined;
  sessionId?: string | undefined;
  ipAddress?: string | undefined;
  userAgent?: string | undefined;
  details: Record<string, unknown>;
  resolved: boolean;
  resolvedAt?: number | undefined;
  resolvedBy?: string | undefined;
}

export interface ThreatPattern {
  id: string;
  name: string;
  description: string;
  events: SecurityEvent[];
  riskScore: number;
  detectedAt: number;
  status: 'active' | 'investigating' | 'resolved' | 'false_positive';
}

export interface SecurityMetrics {
  totalEvents: number;
  eventsByType: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  activeThreats: number;
  resolvedThreats: number;
  averageResolutionTime: number;
  topSources: Array<{ source: string; count: number; }>;
}

class ThreatMonitor {
  private events: Map<string, SecurityEvent> = new Map();
  private patterns: Map<string, ThreatPattern> = new Map();
  private subscribers: Map<string, (event: SecurityEvent) => void> = new Map();
  private patternDetectionInterval: NodeJS.Timeout | null = null;
  private maxEvents = 10000; // Prevent memory issues
  private patternDetectionRules: Array<{
    name: string;
    condition: (events: SecurityEvent[]) => boolean;
    riskScore: number;
  }> = [];

  constructor() {
    this.initializePatternDetection();
    this.setupDefaultPatternRules();
  }

  /**
   * Log a security event
   */
  logEvent(event: Omit<SecurityEvent, 'id' | 'timestamp' | 'resolved'>): string {
    const securityEvent: SecurityEvent = {
      id: nanoid(),
      timestamp: Date.now(),
      resolved: false,
      ...event
    };

    this.events.set(securityEvent.id, securityEvent);
    this.enforceEventLimit();
    this.notifySubscribers(securityEvent);
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[SECURITY] ${securityEvent.severity.toUpperCase()}: ${securityEvent.type} from ${securityEvent.source}`, securityEvent.details);
    }

    return securityEvent.id;
  }

  /**
   * Subscribe to security events
   */
  subscribe(id: string, callback: (event: SecurityEvent) => void): void {
    this.subscribers.set(id, callback);
  }

  /**
   * Unsubscribe from security events
   */
  unsubscribe(id: string): void {
    this.subscribers.delete(id);
  }

  /**
   * Get events by criteria
   */
  getEvents(criteria?: {
    type?: SecurityEvent['type'];
    severity?: SecurityEvent['severity'];
    userId?: string;
    timeRange?: { start: number; end: number; };
    resolved?: boolean;
    limit?: number;
  }): SecurityEvent[] {
    let events = Array.from(this.events.values());

    if (criteria) {
      if (criteria.type) {
        events = events.filter(e => e.type === criteria.type);
      }
      if (criteria.severity) {
        events = events.filter(e => e.severity === criteria.severity);
      }
      if (criteria.userId) {
        events = events.filter(e => e.userId === criteria.userId);
      }
      if (criteria.timeRange) {
        const { start, end } = criteria.timeRange;
        events = events.filter(e => 
          e.timestamp >= start && 
          e.timestamp <= end
        );
      }
      if (criteria.resolved !== undefined) {
        events = events.filter(e => e.resolved === criteria.resolved);
      }
    }

    // Sort by timestamp (newest first)
    events.sort((a, b) => b.timestamp - a.timestamp);

    if (criteria?.limit) {
      events = events.slice(0, criteria.limit);
    }

    return events;
  }

  /**
   * Resolve a security event
   */
  resolveEvent(eventId: string, resolvedBy: string): boolean {
    const event = this.events.get(eventId);
    if (!event) return false;

    event.resolved = true;
    event.resolvedAt = Date.now();
    event.resolvedBy = resolvedBy;
    
    this.events.set(eventId, event);
    return true;
  }

  /**
   * Get security metrics
   */
  getMetrics(timeRange?: { start: number; end: number; }): SecurityMetrics {
    let events = Array.from(this.events.values());
    
    if (timeRange) {
      events = events.filter(e => 
        e.timestamp >= timeRange.start && 
        e.timestamp <= timeRange.end
      );
    }

    const eventsByType: Record<string, number> = {};
    const eventsBySeverity: Record<string, number> = {};
    const sourceCount: Record<string, number> = {};
    let totalResolutionTime = 0;
    let resolvedCount = 0;

    events.forEach(event => {
      // Count by type
      eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
      
      // Count by severity
      eventsBySeverity[event.severity] = (eventsBySeverity[event.severity] || 0) + 1;
      
      // Count by source
      sourceCount[event.source] = (sourceCount[event.source] || 0) + 1;
      
      // Calculate resolution time
      if (event.resolved && event.resolvedAt) {
        totalResolutionTime += event.resolvedAt - event.timestamp;
        resolvedCount++;
      }
    });

    const topSources = Object.entries(sourceCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([source, count]) => ({ source, count }));

    return {
      totalEvents: events.length,
      eventsByType,
      eventsBySeverity,
      activeThreats: this.patterns.size,
      resolvedThreats: Array.from(this.patterns.values()).filter(p => p.status === 'resolved').length,
      averageResolutionTime: resolvedCount > 0 ? totalResolutionTime / resolvedCount : 0,
      topSources
    };
  }

  /**
   * Get detected threat patterns
   */
  getThreatPatterns(): ThreatPattern[] {
    return Array.from(this.patterns.values())
      .sort((a, b) => b.riskScore - a.riskScore);
  }

  /**
   * Add custom pattern detection rule
   */
  addPatternRule(rule: {
    name: string;
    condition: (events: SecurityEvent[]) => boolean;
    riskScore: number;
  }): void {
    this.patternDetectionRules.push(rule);
  }

  /**
   * Initialize pattern detection
   */
  private initializePatternDetection(): void {
    this.patternDetectionInterval = setInterval(() => {
      this.detectPatterns();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Setup default pattern detection rules
   */
  private setupDefaultPatternRules(): void {
    // Multiple failed authentication attempts
    this.addPatternRule({
      name: 'Brute Force Attack',
      condition: (events) => {
        const authFailures = events.filter(e => 
          e.type === 'authentication' && 
          e.severity === 'high' &&
          Date.now() - e.timestamp < 300000 // Last 5 minutes
        );
        return authFailures.length >= 5;
      },
      riskScore: 8
    });

    // Rapid CSP violations
    this.addPatternRule({
      name: 'XSS Attack Attempt',
      condition: (events) => {
        const cspViolations = events.filter(e => 
          e.type === 'csp_violation' &&
          Date.now() - e.timestamp < 60000 // Last minute
        );
        return cspViolations.length >= 3;
      },
      riskScore: 7
    });

    // Rate limit violations
    this.addPatternRule({
      name: 'DDoS Attack',
      condition: (events) => {
        const rateLimitViolations = events.filter(e => 
          e.type === 'rate_limit' &&
          Date.now() - e.timestamp < 60000 // Last minute
        );
        return rateLimitViolations.length >= 10;
      },
      riskScore: 9
    });
  }

  /**
   * Detect threat patterns
   */
  private detectPatterns(): void {
    const recentEvents = this.getEvents({
      timeRange: { start: Date.now() - 600000, end: Date.now() }, // Last 10 minutes
      resolved: false
    });

    this.patternDetectionRules.forEach(rule => {
      if (rule.condition(recentEvents)) {
        const patternId = nanoid();
        const pattern: ThreatPattern = {
          id: patternId,
          name: rule.name,
          description: `Detected ${rule.name} pattern based on recent security events`,
          events: recentEvents.filter(e => this.isEventRelevantToPattern(e, rule.name)),
          riskScore: rule.riskScore,
          detectedAt: Date.now(),
          status: 'active'
        };

        this.patterns.set(patternId, pattern);

        // Log the pattern detection as a critical event
        this.logEvent({
          type: 'suspicious_activity',
          severity: 'critical',
          source: 'threat_monitor',
          details: {
            pattern: rule.name,
            riskScore: rule.riskScore,
            eventCount: pattern.events.length
          }
        });
      }
    });
  }

  /**
   * Check if event is relevant to a pattern
   */
  private isEventRelevantToPattern(event: SecurityEvent, patternName: string): boolean {
    switch (patternName) {
      case 'Brute Force Attack':
        return event.type === 'authentication';
      case 'XSS Attack Attempt':
        return event.type === 'csp_violation';
      case 'DDoS Attack':
        return event.type === 'rate_limit';
      default:
        return false;
    }
  }

  /**
   * Notify subscribers of new events
   */
  private notifySubscribers(event: SecurityEvent): void {
    this.subscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error notifying security event subscriber:', error);
      }
    });
  }

  /**
   * Enforce event limit to prevent memory issues
   */
  private enforceEventLimit(): void {
    if (this.events.size > this.maxEvents) {
      const events = Array.from(this.events.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp); // Oldest first
      
      const toRemove = events.slice(0, this.events.size - this.maxEvents + 1000); // Remove oldest 1000
      toRemove.forEach(([id]) => this.events.delete(id));
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.patternDetectionInterval) {
      clearInterval(this.patternDetectionInterval);
      this.patternDetectionInterval = null;
    }
    this.events.clear();
    this.patterns.clear();
    this.subscribers.clear();
  }
}

// Export singleton instance
export const threatMonitor = new ThreatMonitor();
export default threatMonitor;